<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { onMount } from 'svelte';
  import { replace, push, pop } from 'svelte-spa-router';
  import { session } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import { isExpiringSoon } from '../lib/session';
  import { formatDate } from '../lib/format';
  import type { AdminUserDTO, PaymentStatus, SubscriptionStatus } from '../lib/types';

  interface Props {
    params?: { id?: string };
  }
  let { params }: Props = $props();
  const telegramId = $derived(params?.id ?? '');

  let user = $state<AdminUserDTO | null>(null);
  let loading = $state(true);
  let notFound = $state(false);
  let error = $state<string | null>(null);

  // All emails/phones (detail endpoint provides arrays; fall back to primary).
  const emails = $derived(
    user ? (user.emails?.length ? user.emails : user.email ? [user.email] : []) : [],
  );
  const phones = $derived(
    user ? (user.phones?.length ? user.phones : user.phone ? [user.phone] : []) : [],
  );

  const STATUS_LABEL: Record<SubscriptionStatus, string> = {
    active: 'Активна',
    inactive: 'Закінчилась',
    none: 'Без підписки',
  };
  const STATUS_CLASS: Record<SubscriptionStatus, string> = {
    active: 'badge-active',
    inactive: 'badge-expired',
    none: 'badge-muted',
  };

  const payments = $derived(user?.payments ?? []);
  // Total paid = succeeded amounts minus refunds.
  const paidTotalCents = $derived(
    payments.reduce(
      (sum, p) =>
        sum + (p.status === 'succeeded' ? p.amountCents : p.status === 'refunded' ? -p.amountCents : 0),
      0,
    ),
  );
  const PAY_LABEL: Record<PaymentStatus, string> = {
    succeeded: 'Успішно',
    refunded: 'Повернення',
    failed: 'Відхилено',
    pending: 'Очікує',
  };
  const PAY_CLASS: Record<PaymentStatus, string> = {
    succeeded: 'badge-active',
    refunded: 'badge-expired',
    failed: 'badge-expired',
    pending: 'badge-muted',
  };
  function uah(cents: number): string {
    return `₴${(cents / 100).toLocaleString('uk-UA', { maximumFractionDigits: 2 })}`;
  }

  function initials(u: AdminUserDTO): string {
    const a = u.name?.charAt(0) ?? '';
    const b = u.lastName?.charAt(0) ?? '';
    return (a + b).toUpperCase() || '?';
  }

  // Back to wherever we came from (dashboard drill-in / subscriptions / card);
  // fall back to the dashboard if opened fresh with no in-app history.
  function goBack(): void {
    if (window.history.length > 1) pop();
    else replace('/admin/dashboard');
  }

  onMount(async () => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    if (!telegramId) {
      notFound = true;
      loading = false;
      return;
    }
    try {
      user = await api.adminUser(telegramId);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        notFound = true;
      } else if (err instanceof ApiError && err.status === 403) {
        replace('/home');
        return;
      } else {
        error = 'Не вдалось завантажити контакт';
      }
    } finally {
      loading = false;
    }
  });
</script>

<div class="screen">
  <div class="admin-header">
    <button type="button" class="back-link" onclick={goBack}>← Назад</button>
    <div class="header-title">Контакт</div>
    <a href="#/profile" class="admin-exit">Вийти</a>
  </div>

  <div class="scroll">
    {#if loading}
      <div class="placeholder">Завантажуємо…</div>
    {:else if notFound}
      <div class="placeholder">Контакт не знайдено</div>
    {:else if error}
      <div class="placeholder error">{error}</div>
    {:else if user}
      <!-- Hero: avatar + name + status -->
      <div class="hero">
        <div class="hero-av">
          {#if user.photoUrl}<img src={user.photoUrl} alt="" />{:else}{initials(user)}{/if}
        </div>
        <div class="hero-name">{user.fullName}</div>
        {#if user.username}<div class="hero-username">@{user.username}</div>{/if}
        <div class="hero-badges">
          <span class="badge {STATUS_CLASS[user.status]}">{STATUS_LABEL[user.status]}</span>
          {#if user.isAdmin}<span class="badge badge-admin"><Icon name="shield-check" size="xs" /> Адмін</span>{/if}
          {#if user.telegramId.startsWith('-')}<span class="badge badge-muted"><Icon name="credit-card" size="xs" /> Контакт з оплати</span>{/if}
        </div>
      </div>

      <!-- Контакти -->
      <div class="section-title">Контакти</div>
      <div class="info-block">
        <div class="info-row">
          <div class="info-label">{emails.length > 1 ? 'Email-и' : 'Email'}</div>
          <div class="info-value">
            {#if emails.length}
              <div class="multi">
                {#each emails as e (e)}
                  <a href="mailto:{e}">{e}</a>
                {/each}
              </div>
            {:else}—{/if}
          </div>
        </div>
        <div class="info-row">
          <div class="info-label">{phones.length > 1 ? 'Телефони' : 'Телефон'}</div>
          <div class="info-value">
            {#if phones.length}
              <div class="multi">
                {#each phones as ph (ph)}
                  <a href="tel:{ph}">{ph}</a>
                {/each}
              </div>
            {:else}—{/if}
          </div>
        </div>
        <div class="info-row">
          <div class="info-label">Telegram ID</div>
          <div class="info-value mono">{user.telegramId}</div>
        </div>
        <div class="info-row">
          <div class="info-label">Username</div>
          <div class="info-value">
            {#if user.username}
              <a href="https://t.me/{user.username}" target="_blank" rel="noopener">@{user.username}</a>
            {:else}—{/if}
          </div>
        </div>
      </div>

      <!-- Підписка -->
      <div class="section-title">Підписка</div>
      <div class="info-block">
        <div class="info-row">
          <div class="info-label">Статус</div>
          <div class="info-value">
            <span class="badge {STATUS_CLASS[user.status]}">{STATUS_LABEL[user.status]}</span>
          </div>
        </div>
        <div class="info-row">
          <div class="info-label">Тариф</div>
          <div class="info-value">{user.tariff ?? '—'}</div>
        </div>
        <div class="info-row">
          <div class="info-label">Початок</div>
          <div class="info-value">{formatDate(user.subscriptionStartDate)}</div>
        </div>
        <div class="info-row">
          <div class="info-label">Закінчення</div>
          <div
            class="info-value"
            class:red={user.status === 'inactive'}
            class:yellow={user.status === 'active' && isExpiringSoon(user.subscriptionEndDate)}
          >
            {formatDate(user.subscriptionEndDate)}
          </div>
        </div>
      </div>

      <!-- Оплати -->
      <div class="section-title">
        Оплати{payments.length ? ` · ${payments.length} · ${uah(paidTotalCents)}` : ''}
      </div>
      {#if payments.length === 0}
        <div class="info-block">
          <div class="info-row"><div class="info-value muted-row">Оплат немає</div></div>
        </div>
      {:else}
        <div class="pay-list">
          {#each payments as p (p.id)}
            <div
              class="pay-row"
              role="button"
              tabindex="0"
              onclick={() => push(`/admin/payment/${p.id}`)}
              onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && push(`/admin/payment/${p.id}`)}
            >
              <div class="pay-main">
                <div class="pay-product">{p.product?.title || p.rawProduct || '—'}</div>
                <div class="pay-date">{formatDate(p.occurredAt)}</div>
              </div>
              <div class="pay-right">
                <div class="pay-amount" class:refund={p.status === 'refunded'}>
                  {p.status === 'refunded' ? '−' : ''}{uah(p.amountCents)}
                </div>
                <span class="badge {PAY_CLASS[p.status]}">{PAY_LABEL[p.status]}</span>
              </div>
            </div>
          {/each}
        </div>
      {/if}

      <!-- Звідки прийшов контакт (first-touch). Фіксується один раз і не
           перезаписується, тож це саме «перший дотик», а не остання оплата. -->
      <div class="section-title">Звідки прийшов</div>
      <div class="info-block">
        <!-- Показуємо мітки, а не сам факт замка: контакт, що прийшов без
             реклами, теж має firstTouchAt — але дивитись там нема на що. -->
        {#if user.firstUtmSource || user.firstUtmMedium || user.firstUtmCampaign || user.firstUtmContent || user.firstUtmTerm}
          <div class="info-row">
            <div class="info-label">utm_source</div>
            <div class="info-value">{user.firstUtmSource || '—'}</div>
          </div>
          <div class="info-row">
            <div class="info-label">utm_medium</div>
            <div class="info-value">{user.firstUtmMedium || '—'}</div>
          </div>
          <div class="info-row">
            <div class="info-label">utm_campaign</div>
            <div class="info-value">{user.firstUtmCampaign || '—'}</div>
          </div>
          <div class="info-row">
            <div class="info-label">utm_content</div>
            <div class="info-value">{user.firstUtmContent || '—'}</div>
          </div>
          <div class="info-row">
            <div class="info-label">utm_term</div>
            <div class="info-value">{user.firstUtmTerm || '—'}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Мітки зафіксовано</div>
            <div class="info-value">
              {formatDate(user.firstTouchAt)}
              {#if user.firstTouchVia}
                · {user.firstTouchVia === 'payment' ? 'з оплати' : 'з SendPulse'}
              {/if}
            </div>
          </div>
        {:else}
          <div class="info-row">
            <div class="info-value muted-row">
              Прийшов не з розміченої реклами — UTM-міток не було.
            </div>
          </div>
        {/if}
      </div>

      <!-- Метадані -->
      <div class="section-title">У базі</div>
      <div class="info-block">
        <div class="info-row">
          <div class="info-label">Створено</div>
          <div class="info-value">{formatDate(user.createdAt)}</div>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px;
    color: var(--text-light);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .back-link {
    font-size: 12px;
    color: rgba(251, 246, 236, 0.9);
    text-decoration: none;
    padding: 4px 0;
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
  }
  .header-title {
    flex: 1;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
  }
  .admin-exit {
    font-size: 11px;
    opacity: 0.85;
    background: rgba(251, 246, 236, 0.1);
    padding: 5px 11px;
    border-radius: 7px;
    color: var(--text-light);
    text-decoration: none;
  }

  .scroll {
    flex: 1;
    overflow-y: auto;
    padding: 18px;
  }
  .scroll::-webkit-scrollbar { width: 3px; }
  .scroll::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 2px; }

  .hero {
    text-align: center;
    margin-bottom: 22px;
  }
  .hero-av {
    width: 84px;
    height: 84px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-light);
    font-size: 30px;
    font-weight: 600;
    margin: 0 auto 12px;
    overflow: hidden;
    border: 3px solid var(--pu-light);
  }
  .hero-av img { width: 100%; height: 100%; object-fit: cover; }
  .hero-name {
    font-size: 19px;
    font-weight: 700;
    color: var(--text);
  }
  .hero-username {
    font-size: 13px;
    color: var(--text-muted);
    margin-top: 4px;
  }
  .hero-badges {
    display: flex;
    justify-content: center;
    gap: 6px;
    margin-top: 10px;
  }

  .section-title {
    font-size: 11px;
    font-weight: 700;
    color: var(--text-muted);
    letter-spacing: 1px;
    text-transform: uppercase;
    margin: 0 0 8px 4px;
  }
  .info-block {
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 18px;
  }
  .info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 16px;
    border-bottom: 1px solid var(--border);
    font-size: 13px;
    gap: 12px;
  }
  .info-row:last-child { border-bottom: none; }
  .info-label {
    color: var(--text-muted);
    font-weight: 500;
    flex-shrink: 0;
  }
  .info-value {
    color: var(--text);
    font-weight: 600;
    text-align: right;
    word-break: break-word;
    min-width: 0;
  }
  .info-value a {
    color: var(--accent-text);
    text-decoration: none;
  }
  .info-value a:hover {
    text-decoration: underline;
  }
  .multi {
    display: flex;
    flex-direction: column;
    gap: 3px;
    align-items: flex-end;
  }
  .info-value.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }
  .info-value.yellow { color: var(--status-expiring); }
  .info-value.red { color: var(--status-inactive); }

  .badge {
    font-size: 10px;
    padding: 3px 8px;
    border-radius: 10px;
    font-weight: 600;
    white-space: nowrap;
  }
  .badge-active { background: var(--green-light); color: var(--green); }
  .badge-expired { background: var(--danger-bg); color: var(--status-inactive); }
  .badge-muted { background: var(--bg); color: var(--text-muted); border: 1px solid var(--border); }
  .badge-admin {
    background: linear-gradient(135deg, var(--dark3), var(--pu3));
    color: var(--text-light);
  }

  .muted-row { color: var(--text-muted); font-weight: 500; text-align: left; width: 100%; }
  .pay-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 18px; }
  .pay-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    cursor: pointer;
  }
  .pay-row:hover { border-color: var(--outline); }
  .pay-main { min-width: 0; flex: 1; }
  .pay-product {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .pay-date { font-size: 11px; color: var(--text-muted); margin-top: 3px; }
  .pay-right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }
  .pay-amount { font-size: 14px; font-weight: 700; color: var(--text); }
  .pay-amount.refund { color: var(--status-inactive); }

  .placeholder {
    padding: 40px 20px;
    text-align: center;
    color: var(--text-muted);
    font-size: 13px;
  }
  .placeholder.error { color: var(--status-inactive); }
</style>
